/* eslint-disable max-len */
/*
 * Package Import
 */
import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import format from 'date-fns/format';
import { Button } from '@oclock/crumble';

/*
 * Local Import
 */
import {
  courseFormDataSchema,
  CourseFormData,
  CourseFormDataValidated,
  courseFormDataValidatedSchema,
} from 'src/schemas/Entities/Course';

import { CustomSelect } from 'src/components/Admin/Modals/FormElements/Selects/CustomSelect';
import { Modal } from 'src/components/Admin/Modals/Modal';
import * as F from 'src/components/Admin/Modals/FormElements';
import { MongoId } from 'src/schemas/Entities/utils';
import { useStore } from 'src/localStateManagers/store';
import { dangerousTransformObjectInObjectAny } from 'src/utils/types';

// Helpers
import { getRoute } from 'src/utils/routes';
import { LIMITATIONS_FEATURES } from 'src/constants/limitations';

// Queries
import { useGetLimitations } from 'src/serverStateManagers/queries/Limitations/useGetLimitations';
import { useGetPromotions } from 'src/serverStateManagers/queries/Promotions/useGetPromotions';
import { useGetUsers } from 'src/serverStateManagers/queries/Users/useGetUsers';
import { useGetCourses } from 'src/serverStateManagers/queries/Courses/useGetCourses';
import { useUpdateCourse } from 'src/serverStateManagers/mutations/update/Courses/useUpdateCourse';

import { coursesUpdateFormData } from './config';
import { getFormattedTimeslots, isDateReadOnly } from './utils';

/*
 * Code
 */
type UpdateCourseProps = {
  isOpen: boolean;
  idToUpdate: MongoId | null;
};
const UpdateCourse = ({ isOpen, idToUpdate }: UpdateCourseProps): JSX.Element | null => {
  const updateCourseMutation = useUpdateCourse();
  const client = useStore((state) => state.client);
  const history = useHistory();

  // Config
  const { header, inputs } = coursesUpdateFormData;
  const { id, title, date, selectTeacher, selectHelpers, selectPromotion } = inputs;

  // Entity Data
  const { data: coursesData } = useGetCourses();
  const courseData = coursesData?.find((course) => course.id === idToUpdate);

  // Selects Data
  const { data: limitationsData } = useGetLimitations();
  const { data: promotionsData } = useGetPromotions();
  const { data: usersData } = useGetUsers();
  const maxParticipantsData = useMemo(
    () =>
      limitationsData
      && limitationsData.find(
        (limitation) => limitation.feature === LIMITATIONS_FEATURES.MAX_PARTICIPANTS_IN_COURSE,
      ),
    [limitationsData],
  );
  const activePromotionsData = useMemo(
    () => promotionsData?.filter((promoData) => !promoData.deactivatedAt),
    [promotionsData],
  );
  const activeUsersData = useMemo(
    () => usersData?.filter((userData) => !userData.deactivatedAt),
    [usersData],
  );

  // Selects Options
  const promoOptions = selectPromotion.getPromoOptions(promotionsData);
  const activePromoOptions = selectPromotion.getPromoOptions(activePromotionsData);
  const teacherOptions = selectTeacher.getPedaOptions(usersData);
  const activeTeacherOptions = selectTeacher.getPedaOptions(activeUsersData);
  const helpersOptions = selectHelpers.getPedaOptions(usersData);
  const activeHelpersOption = selectHelpers.getPedaOptions(activeUsersData);

  // Selects Current Selected Options
  const currentPromotionOption = promoOptions.find(
    (option) => option.value === courseData?.promotion.id,
  );
  const currentTeacherOption = teacherOptions.find(
    (option) => option.value === courseData?.teacher.id,
  );
  const currentHelpersOptions = helpersOptions.filter((option) =>
    courseData?.helpers.map((helper) => (helper ? helper.id : null))?.includes(option.value),
  );

  // Format timeslots when course is updated or modal opened state change
  const formattedTimeSlots = useMemo(
    () => getFormattedTimeslots(courseData?.timeslots),
    [courseData?.timeslots, isOpen],
  );

  // Form
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitSuccessful, isValid },
    clearErrors,
    reset,
    trigger,
    setValue,
  } = useForm({
    resolver: zodResolver(courseFormDataSchema),
    mode: 'onChange',
    defaultValues: {
      timeslots: formattedTimeSlots,
    },
  });

  // To bind form hook to timeslots field
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'timeslots',
    keyName: 'fieldId',
  });

  // Watch formatted time slots and set value to timeslots field
  // Only if fields is actually empty
  useEffect(() => {
    if (fields.length === 0) {
      setValue('timeslots', formattedTimeSlots);
    }
  }, [formattedTimeSlots]);
  useEffect(() => {
    setValue('timeslots', formattedTimeSlots);
  }, [idToUpdate]);

  // Trigger validation to get errors on timeslots
  //  b/c mode onChange doesn't trigger validation on linked path
  useEffect(() => {
    trigger('timeslots');
    trigger('limitationSimultaneousCourses');
    trigger('limitationTotalHours');
  }, [fields]);

  useEffect(() => {
    trigger('promotion');
    trigger('timeslots');
    trigger('limitationTotalHours');
    trigger('limitationSimultaneousCourses');
  }, [watch('date')]);

  const onSubmit = (data: CourseFormData): void => {
    const courseFormDataValidatedResult: CourseFormDataValidated = courseFormDataValidatedSchema.parse(data);
    clearErrors();
    if (idToUpdate) {
      updateCourseMutation.mutate({
        courseId: idToUpdate,
        courseFormData: courseFormDataValidatedResult,
      });
    }
  };

  const handleClose = (evt: React.MouseEvent): void => {
    evt.preventDefault();
    reset();
    clearErrors();
    history.push(getRoute('coursesTable').getUrl({}));
  };

  /**
   * LifeCycles
   */
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
      history.push(getRoute('coursesTable').getUrl({}));
    }
  }, [isSubmitSuccessful, reset, history]);

  /**
   * Render
   */
  // Force to have id and course found before you can display modal
  // Import for default value in Selects
  if (!idToUpdate || !courseData) return null;
  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose}>
      <F.Form onSubmit={handleSubmit(onSubmit)}>
        <F.Header>{header}</F.Header>

        {/* Limitations */}
        {maxParticipantsData && maxParticipantsData.active && maxParticipantsData.quantity && (
          <F.Description>
            Votre abonnement actuel vous permet d’accueillir {maxParticipantsData.quantity}{' '}
            participants au total dans la classe virtuelle, formateurs inclus.
          </F.Description>
        )}

        {/* Fields */}
        <F.Field>
          <F.Input ref={register()} name={id.title} type={id.type} defaultValue={courseData?.id} />
        </F.Field>
        <F.Field>
          <F.Label htmlFor={title.title}>{title.display}</F.Label>
          <F.Input
            className={errors.title ? 'is-error' : undefined}
            ref={register()}
            name={title.title}
            type={title.type}
            defaultValue={courseData?.title}
          />
          {errors.title?.message && <F.ErrorMessage>{errors.title.message}</F.ErrorMessage>}
        </F.Field>
        <F.Line>
          <F.Field key={selectPromotion.title} isLeft>
            <F.Label htmlFor={selectPromotion.title}>{selectPromotion.display}</F.Label>
            <CustomSelect
              isMulti={selectPromotion.isMulti}
              data={activePromoOptions}
              selectName={selectPromotion.title}
              control={control}
              defaultValue={currentPromotionOption}
              isError={!!errors.promotion}
              optionsExtraData={selectPromotion.getPromoOptionsTooltipsInfos(promotionsData)}
              mustValidateAtDisplay={trigger}
            />
            {errors.promotion?.message && (
              <F.ErrorMessage>{errors.promotion.message}</F.ErrorMessage>
            )}
          </F.Field>
          <F.Field>
            <F.Label htmlFor={selectTeacher.title}>{selectTeacher.display}</F.Label>
            <CustomSelect
              isMulti={selectTeacher.isMulti}
              data={activeTeacherOptions}
              selectName={selectTeacher.title}
              control={control}
              optionsToDisable={watch(selectTeacher.selectToWatch)}
              defaultValue={currentTeacherOption}
              isError={!!errors.teacher}
              mustValidateAtDisplay={trigger}
            />
            {errors.teacher?.message && <F.ErrorMessage>{errors.teacher.message}</F.ErrorMessage>}
          </F.Field>
        </F.Line>
        <F.Line>
          <F.Field isLeft>
            <F.Label htmlFor={date.title}>{date.display}</F.Label>
            <F.Input
              className={
                // eslint-disable-next-line no-nested-ternary
                errors.date
                  ? 'is-error'
                  : courseData && isDateReadOnly(courseData.date, client)
                    ? 'readOnly'
                    : undefined
              }
              ref={register()}
              name={date.title}
              type={date.type}
              readOnly={courseData && isDateReadOnly(courseData.date, client)}
              tabIndex={courseData && isDateReadOnly(courseData.date, client) ? -1 : 0}
              defaultValue={courseData ? format(courseData.date, 'yyyy-MM-dd') : ''}
              min={
                courseData && !isDateReadOnly(courseData.date, client)
                  ? date.getMinDate(
                    promotionsData,
                    currentPromotionOption,
                    watch(selectPromotion.title),
                  )
                  : undefined
              }
              max={
                courseData && !isDateReadOnly(courseData.date, client)
                  ? date.getMaxDate(
                    promotionsData,
                    currentPromotionOption,
                    watch(selectPromotion.title),
                  )
                  : undefined
              }
            />
            {errors.date?.message && <F.ErrorMessage>{errors.date.message}</F.ErrorMessage>}
          </F.Field>
          <F.Field>
            <F.Label htmlFor={selectHelpers.title}>{selectHelpers.display}</F.Label>
            <CustomSelect
              isMulti={selectHelpers.isMulti}
              data={activeHelpersOption}
              selectName={selectHelpers.title}
              control={control}
              optionsToDisable={watch(selectHelpers.selectToWatch)}
              defaultValue={currentHelpersOptions}
              isError={!!errors.helpers}
              mustValidateAtDisplay={trigger}
            />
            {!!errors.helpers
              && errors.helpers?.map(
                (error, index) =>
                  error?.message && <F.ErrorMessage key={index}>{error.message}</F.ErrorMessage>,
              )}
          </F.Field>
        </F.Line>
        <F.Field>
          <F.TimeslotsList
            fields={fields}
            append={append}
            remove={remove}
            register={register}
            watch={watch}
            errors={errors}
          />
          {/* Errors messages */}
        </F.Field>
        {dangerousTransformObjectInObjectAny(errors)?.limitationSimultaneousCourses?.message && (
          <F.ErrorMessage>
            {dangerousTransformObjectInObjectAny(errors).limitationSimultaneousCourses.message}
          </F.ErrorMessage>
        )}
        {dangerousTransformObjectInObjectAny(errors).limitationTotalHours?.message && (
          <F.ErrorMessage>
            {dangerousTransformObjectInObjectAny(errors).limitationTotalHours.message}
          </F.ErrorMessage>
        )}

        {/* CTAs */}
        <F.Buttons>
          <Button type="button" onClick={handleClose} variant="secondary" fullWidth>
            Annuler
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            variant="primary"
            disabled={!isValid}
            fullWidth
          >
            Enregistrer
          </Button>
        </F.Buttons>
      </F.Form>
    </Modal>
  );
};

/*
 * Export
 */
export default UpdateCourse;
